import React, { useCallback, useRef } from 'react';
import { defaultProps } from '@jira/platform__default-props/src/defaultProps.tsx';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import {
	GLOBAL_TRANSITION_PORT,
	GLOBAL_TRANSITION_PORT_OFFSET,
	STATE_COLOURS,
	TRANSITION_STROKE_WIDTH,
} from './ellipsis.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import { type Point, toPoint, type TransitionIcons, type ValidationFlag } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { DiagramArrow } from './DiagramArrow.tsx';
import { IconSet } from './IconSet.tsx';
import { truncateLabel } from './isEveryPropertyDefined.tsx';
import {
	GLOBAL_TRANSITION_ROUTE_HEIGHT_DISTANCE as ROUTE_HEIGHT,
	globalTransitionRoute,
} from './globalTransitionRoute.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';
import useLayoutWidth from './useLayoutWidth.tsx';

const anyTransitionLabel = defineMessage({
	id: 'accessible-workflow-diagram.diagram.global-transition.any-transition-label',
	defaultMessage: 'Any',
	description:
		'Message that indicates this transition can be used from "Any" status in the workflow.',
});

const MIN_LOZENGE_WIDTH = 40;
const LOZENGE_HEIGHT = 24;
const LOZENGE_BORDER_RADIUS = LOZENGE_HEIGHT / 2;
const ERROR_RING_DASH_ARRAY = '3,2';
const FOCUS_RING_WIDTH = 4;
const TEXT_ICON_BUFFER = 8;
const LOZENGE_PADDING = 10; // total padding around left and right of lozenge, L + R value

export interface Props {
	id: string;
	flag?: ValidationFlag;
	isHovered?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	isHighlighted: boolean;
	portPosition: Point | undefined;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
	icons?: TransitionIcons[];
}

const GlobalTransition = ({
	id,
	flag,
	isHovered = false,
	isImmutable,
	isHighlighted,
	portPosition,
	isSelected = false,
	isTargeted = false,
	onSelect,
	onHover,
	icons = defaultProps.emptyArray,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const stroke = useStrokeColor({ flag, isHighlighted, isSelected, isHovered });
	const textRef = useRef<SVGTextElement | null>(null);
	const iconsRef = useRef<SVGGElement | null>(null);
	const lozengeText = truncateLabel(formatMessage(anyTransitionLabel));
	const textWidth = useLayoutWidth(textRef, [icons]);
	const iconsWidth = useLayoutWidth(iconsRef, [icons]);

	const lozengeWidth = Math.max(
		MIN_LOZENGE_WIDTH + iconsWidth,
		textWidth + iconsWidth + (iconsWidth > 0 ? TEXT_ICON_BUFFER : 0) + LOZENGE_PADDING,
	);

	const routeWidth = lozengeWidth / 2;

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [id, isImmutable, onSelect]);

	const onMouseEnter = useCallback(() => {
		onHover?.({ id, type: EDGE_TYPE });
	}, [id, onHover]);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	if (!portPosition) {
		return null;
	}

	const path = globalTransitionRoute(portPosition, GLOBAL_TRANSITION_PORT_OFFSET, lozengeWidth / 2);

	const textX =
		iconsWidth > 0
			? portPosition.x + iconsWidth + TEXT_ICON_BUFFER + LOZENGE_PADDING / 2
			: portPosition.x + (lozengeWidth - textWidth) / 2;
	const textY = portPosition.y - ROUTE_HEIGHT - 8;
	const lozengeX = portPosition.x + routeWidth - lozengeWidth / 2;
	const lozengeY = portPosition.y - ROUTE_HEIGHT - LOZENGE_HEIGHT;

	return (
		<g onClick={onGroupClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{/* Click and hover area */}
			<path fill="none" stroke="transparent" d={path} strokeWidth="15" />
			{/* Transition line */}
			<path fill="none" stroke={stroke} strokeLinejoin="round" d={path} strokeWidth="2" />
			<DiagramArrow
				stroke={stroke}
				portIndex={GLOBAL_TRANSITION_PORT}
				portPosition={portPosition}
				portOffset={GLOBAL_TRANSITION_PORT_OFFSET}
			/>
			{isTargeted && (
				<rect
					x={lozengeX}
					y={lozengeY}
					width={lozengeWidth}
					height={LOZENGE_HEIGHT}
					rx={LOZENGE_BORDER_RADIUS}
					ry={LOZENGE_BORDER_RADIUS}
					stroke={STATE_COLOURS.focused}
					strokeWidth={FOCUS_RING_WIDTH * 2}
				/>
			)}
			{/* Lozenge bg */}
			<rect
				x={lozengeX}
				y={lozengeY}
				width={lozengeWidth}
				height={LOZENGE_HEIGHT}
				rx={LOZENGE_BORDER_RADIUS}
				ry={LOZENGE_BORDER_RADIUS}
				fill={token('color.background.neutral.bold')}
				strokeWidth={TRANSITION_STROKE_WIDTH}
				stroke={isSelected || flag ? stroke : undefined}
				strokeDasharray={flag && !isSelected ? ERROR_RING_DASH_ARRAY : undefined}
			/>
			{/* Icons */}
			{icons.length && (
				<IconSet
					setRef={(ref) => {
						iconsRef.current = ref;
					}}
					icons={icons}
					inverse
					anchor={toPoint(portPosition.x + LOZENGE_PADDING / 2, textY - LOZENGE_HEIGHT / 2 - 2)}
				/>
			)}
			{/* Lozenge text */}
			<text
				ref={textRef}
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				textAnchor="left"
				fontSize="11px"
				fill={token('color.text.inverse')}
				x={textX}
				y={textY}
			>
				<tspan>{lozengeText}</tspan>
			</text>
		</g>
	);
};

export default GlobalTransition;
